import { ReactNode } from 'react'
import * as marked from 'marked-ast'
import {
  MarkdownToken,
  ImageMarkdownNode,
  LinkMarkdownNode,
  ListMarkdownNode,
  CodeMarkdownNode,
  HeadingMarkdownNode,
  TokenType,
  MarkdownNode,
} from 'marked-ast'
import { decodeHtmlEntities } from 'helpers/formatHelpers'

const MULTIPLE_NEW_LINES_REGEX = /(\r?\n){1,}/g

export function parseMarkdown (markdown: string): MarkdownToken[] {
  return marked.parse(markdown.replace(MULTIPLE_NEW_LINES_REGEX, '$1'))
}

export function hasTokenOfType (markdownToken: MarkdownToken | MarkdownToken[], type: TokenType): boolean {
  const markdownTokens = markdownToken instanceof Array ? markdownToken : [markdownToken]
  for (let m = 0, ml = markdownTokens.length; m < ml; m++) {
    const token = markdownTokens[m]
    if (!token || typeof token === 'string') {
      continue
    }
    if (token.type === type || hasTokenOfType(token.text, type)) {
      return true
    }
  }
  return false
}

export function getTokensOfType (markdownToken: MarkdownToken | MarkdownToken[], type: TokenType, contextTokens: MarkdownToken[] = []): MarkdownToken[] {
  const markdownTokens = markdownToken instanceof Array ? markdownToken : [markdownToken]
  markdownTokens.forEach((token) => {
    if (!token || typeof token === 'string') {
      return
    }
    if (token.type === type) {
      contextTokens.push(token)
    }
    let children
    if (token.type === 'list') {
      children = token.body
    } else if (token.type === 'code') {
      children = token.code
    } else {
      children = token.text
    }
    getTokensOfType(children, type, contextTokens)
  })

  return contextTokens
}

export function renderTokensToPlainText (markdownToken: MarkdownToken | MarkdownToken[]): string {
  const markdownTokens = markdownToken instanceof Array ? markdownToken : [markdownToken]
  return markdownTokens
    .map((token) => typeof token === 'string' ? decodeHtmlEntities(token) : renderTokensToPlainText(token.text))
    .join('')
}

export type RenderToken = (token: MarkdownNode, key: string, renderTokenMap: RenderTokenMap) => ReactNode
type RenderTokenMap = Record<string, RenderToken>

export const TAG_NAME_BY_TOKEN_TYPE = {
  image: 'img',
  link: 'a',
  paragraph: 'div',
  heading: 'h',
  em: 'em',
  strong: 'strong',
  br: 'br',
  hr: 'hr',
  del: 'strike',
  list: 'ul',
  listitem: 'li',
  blockquote: 'blockquote',
  codespan: 'span',
  code: 'code',
  video: undefined,   // TODO: default renderer
}


export function createRenderTokensToJsx (customTokenRenderers?: RenderTokenMap) {
  const defaultRenderFunction: RenderToken = (token: MarkdownNode, key) => {
    const Tag: any = TAG_NAME_BY_TOKEN_TYPE[token.type]
    if (!Tag) {
      return null
    }

    return (
      <Tag key={ key }>{ renderTokensToJsx(token.text) }</Tag>
    )
  }

  const tokenRendererMap: RenderTokenMap = {
    image: (token: ImageMarkdownNode, key: string) => {
      const { href, text } = token
      return (
        <img key={ key } src={ decodeHtmlEntities(href) } alt={ renderTokensToPlainText(text) } />
      )
    },
    link: (token: LinkMarkdownNode, key: string) => {
      return (
        <a key={ key } href={ token.href } target='_blank' rel='noopener noreferrer'>
          { renderTokensToJsx(token.text) }
        </a>
      )
    },
    heading: (token: HeadingMarkdownNode, key) => {
      const Tag: any = 'h' + token.level
      return (
        <Tag key={ key }>{ renderTokensToJsx(token.text) }</Tag>
      )
    },
    list: (token: ListMarkdownNode, key) => {
      const { ordered, body } = token
      const Tag = ordered ? 'ol' : 'ul'
      return (
        <Tag key={ key }>{ renderTokensToJsx(body) }</Tag>
      )
    },
    code: (token: CodeMarkdownNode, key) => {
      const { code, lang } = token
      return (
        <code key={ key } data-lang={ lang }>{ renderTokensToJsx(code) }</code>
      )
    },
    br: () => null,
    hr: () => null,
    default: defaultRenderFunction,
    ...customTokenRenderers,
  }

  function renderTokensToJsx (markdownToken: MarkdownToken | MarkdownToken[]): ReactNode[] {
    const markdownTokens = markdownToken instanceof Array ? markdownToken : [markdownToken]

    return markdownTokens.map((token, t) => {
      if (!token) {
        return null
      }

      if (typeof token === 'string') {
        return (
          <span key={ t }>{ decodeHtmlEntities(token) }</span>
        )
      }

      if (token.type === 'image') {
        const { href } = token
        const url = decodeHtmlEntities(href)
        if (isVideo(url)) {
          token = {
            ...token,
            type: 'video',
          }
        }
      }

      const renderToken = tokenRendererMap[token.type] || defaultRenderFunction
      return renderToken(token, t + '', tokenRendererMap)
    })
  }

  return renderTokensToJsx
}


export type MediaSize = {
  width: number
  height: number
}

export type MaxMediaSize = {
  maxWidth: number
  maxHeight: number
}

const PROTOCOL_REGEX = /^([a-z]+):\/\/.*$/i

export function extractProtocolFromUrl (url: string): string {
  const matches = url.match(PROTOCOL_REGEX)
  return matches ? matches[1].toLowerCase() : location.protocol.slice(0, -1)
}

const VIDEO_ID_REGEX = /^(?:vimeo:\/\/)([^?]+)/i

export function extractVideoIdFromUrl (url: string): string | null {
  const matches = url.match(VIDEO_ID_REGEX)
  return matches ? matches[1] : null
}

type QueryParams = Record<string, string>

export function extractQueryParamsFromUrl (url: string): QueryParams {
  const query = url.split('?').slice(1).join('?')
  const couples = query.split(/&/g)
  const params: QueryParams = {}
  couples.forEach((couple) => {
    const [key, value] = couple.split('=')
    params[key.toLowerCase()] = value
  })

  return params
}

export function extractMediaSizeFromUrl (url: string): MediaSize | null {
  const params = extractQueryParamsFromUrl(url)
  const width = Number(params.width)
  const height = Number(params.height)

  if (width > 0 && height > 0) {
    return { width, height }
  } else {
    return null
  }
}

export function getMediaMaxSize (size: MediaSize, maxAllowedWidth: number): MaxMediaSize {
  const maxWidth = Math.min(size.width, maxAllowedWidth)
  // Don't worry, width guaranteed not equal to zero
  const maxHeight = maxWidth * size.height / size.width
  return { maxWidth, maxHeight }
}

export const isVideo = (url: string): boolean => extractProtocolFromUrl(url) === 'vimeo'
